import React, { useEffect, useRef, useState, useMemo } from 'react';
import { ChevronDown, Hexagon, Plus, Bot, Wrench } from 'lucide-react';
import { createPortal } from 'react-dom';
import EventsSelectorMenu from 'features/analytics/studio/components/query/EventsSelectorMenu';
import FilterBuilder from 'features/agents/WorkflowEditor/FilterBuilder';
import GlobalPropertyFilterModule from 'features/activity/components/GlobalPropertyFilterModule';
import { EventPropertyFilterChip } from 'features/activity/components/EventsFilterModule';
import { getFixedPanelPositionFromRect } from 'features/analytics/studio/components/query/eventSelectorGeometry';
import axiosInstance from 'shared/services/api/axiosInstance';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';

/**
 * TriggerMetricFilter — the EXACT same filter UX from /analytics/insights,
 * adapted for workflow triggers + the Query Data node.
 *
 * Two stacked sections:
 *
 *   1. METRIC          One event / agent run / span — the thing whose
 *                       occurrence triggers the workflow. Pick via the
 *                       Insights EventsSelectorMenu popover. Inline per-metric
 *                       property filters via the entity-aware FilterBuilder
 *                       (works for events, runs AND spans).
 *
 *   2. GLOBAL FILTER   Cross-entity filters (Events, Users, Agents, Spans,
 *                       Cohorts) that further narrow which occurrences
 *                       trigger the workflow. Uses the existing
 *                       GlobalPropertyFilterModule from Activity/Insights
 *                       so the look + behaviour is byte-for-byte identical.
 *
 * Props:
 *   value      { entity, event_name, filters, global_filters }
 *   onChange   (patch) => void   (partial update, parent merges)
 *   teamId     number
 */
export default function TriggerMetricFilter({ value, onChange }) {
  const teamId = getSelectedTeamNumericId();
  const v = value || { entity: 'events', event_name: '', filters: [], global_filters: [] };

  const [eventMenuOpen, setEventMenuOpen] = useState(false);
  const [eventMenuRect, setEventMenuRect] = useState(null);
  const eventTriggerRef = useRef(null);
  const eventMenuRef = useRef(null);

  const [globalFilterOpen, setGlobalFilterOpen] = useState(false);
  const [editingGlobalFilter, setEditingGlobalFilter] = useState(null);
  const globalFilterRef = useRef(null);

  // Fetch the team's event names once.
  const [availableEvents, setAvailableEvents] = useState([]);
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    axiosInstance.get('/insights/events', { params: { team_id: teamId } })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data || res.data?.events || [];
        setAvailableEvents(list.map((e) => typeof e === 'string' ? { name: e } : e));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [teamId]);

  // Outside click for event menu
  useEffect(() => {
    if (!eventMenuOpen) return;
    const onDoc = (e) => {
      if (eventMenuRef.current?.contains(e.target)) return;
      if (eventTriggerRef.current?.contains(e.target)) return;
      setEventMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [eventMenuOpen]);

  const handlePickEvent = (eventName, kind /* event | agent | span */, opts = {}) => {
    const entityMap = { event: 'events', agent: 'runs', span: 'spans' };
    onChange({
      entity: entityMap[kind] || 'events',
      event_name: eventName,
      // Reset filters when changing metric so a stale property/global scope doesn't linger.
      filters: [],
      global_filters: [],
    });
    setEventMenuOpen(false);
  };

  const handleOpenEventMenu = () => {
    if (eventTriggerRef.current) {
      setEventMenuRect(eventTriggerRef.current.getBoundingClientRect());
    }
    setEventMenuOpen(true);
  };

  const metricKindIcon = useMemo(() => {
    if (v.entity === 'spans') return Wrench;
    if (v.entity === 'runs') return Bot;
    return Hexagon;
  }, [v.entity]);
  const MetricIcon = metricKindIcon;

  const metricKindLabel = v.entity === 'spans' ? 'Span'
                       : v.entity === 'runs' ? 'Agent run'
                       : v.entity === 'users' ? 'User'
                       : 'Event';

  const summaryLabel = v.event_name
    ? v.event_name
    : (v.entity === 'users' ? 'Any user' : `Pick an ${metricKindLabel.toLowerCase()}`);

  return (
    <div className="space-y-3">
      {/* ───────── METRIC SECTION ───────── */}
      <div>
        <div className="text-[12px] font-semibold text-[color:var(--text-primary)] mb-1.5">Metric</div>
        <div className="border border-[color:var(--border-default)] rounded-md bg-[color:var(--surface-sunken)] p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] text-[color:var(--text-muted)]">Trigger when</span>
            <button
              ref={eventTriggerRef}
              type="button"
              onClick={handleOpenEventMenu}
              className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] hover:bg-[color:var(--surface-hover)] text-[13px] text-[color:var(--text-primary)] transition-colors"
            >
              <MetricIcon className="w-3.5 h-3.5 text-[color:var(--text-muted)]" />
              <span className="font-medium">{summaryLabel}</span>
              <ChevronDown className="w-3 h-3 text-[color:var(--text-muted)]" />
            </button>
            <span className="text-[11px] text-[color:var(--text-muted)]">happens</span>
          </div>

          {v.event_name && v.entity !== 'users' && (
            <div className="mt-2.5">
              <div className="text-[12px] text-[color:var(--text-muted)] mb-1">
                Filter this {metricKindLabel.toLowerCase()}
              </div>
              <FilterBuilder
                entity={v.entity}
                eventName={v.entity === 'events' ? v.event_name : undefined}
                hideEventPicker
                value={normalizeInlineFilter(v.filters)}
                onChange={(next) => onChange({ filters: next })}
              />
            </div>
          )}
        </div>
      </div>

      {/* ───────── GLOBAL FILTER SECTION ───────── */}
      <div>
        <div className="text-[12px] font-semibold text-[color:var(--text-primary)] mb-1.5">Global filter</div>
        <div className="border border-[color:var(--border-default)] rounded-md bg-[color:var(--surface-sunken)] p-3 space-y-2">
          {(v.global_filters || []).map((gf, idx) => (
            gf.category === 'first_time' || gf.field === 'firstTime' || gf.property === 'firstTime' ? (
              <div key={gf.id || idx} className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                  {idx === 0 ? 'WHERE' : 'AND'}
                </span>
                <div className="inline-flex items-center gap-2 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 py-1.5">
                  <span className="text-[12px] font-medium text-[color:var(--text-primary)]">First time only</span>
                  <button
                    type="button"
                    onClick={() => onChange({ global_filters: v.global_filters.filter((_, i) => i !== idx) })}
                    className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                    title="Remove First time filter"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
            <EventPropertyFilterChip
              key={gf.id || idx}
              filter={gf}
              isFirst={idx === 0}
              compact
              stackedCard
              teamId={teamId}
              eventName={null}
              onEdit={() => {
                setEditingGlobalFilter(gf);
                setGlobalFilterOpen(true);
              }}
              onRemove={() =>
                onChange({ global_filters: v.global_filters.filter((_, i) => i !== idx) })
              }
              onUpdate={(updates) => {
                const next = v.global_filters.map((x, i) => i === idx ? { ...x, ...updates } : x);
                onChange({ global_filters: next });
              }}
            />
            )
          ))}
          <button
            ref={globalFilterRef}
            type="button"
            onClick={() => { setEditingGlobalFilter(null); setGlobalFilterOpen(true); }}
            className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-dashed border-[color:var(--border-default)] hover:border-[color:var(--border-strong)] text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors"
          >
            <Plus className="w-3 h-3" />
            Filter
          </button>
        </div>
      </div>

      {/* ───────── Popovers (portaled, matching insights) ─────────
          getFixedPanelPositionFromRect returns { top, left, width, maxHeightPx }
          but NOT `position: fixed` — without it the wrapper is statically
          positioned, which means z-index is a no-op and the panel ends up
          stacked behind the modal. Set position + a very high z-index so
          it always sits above the (z-60) node config modal AND the global
          filter popup (z-9999) when both are open. */}
      {eventMenuOpen && eventMenuRect && createPortal(
        <div style={{
          position: 'fixed',
          zIndex: 10000,
          ...getFixedPanelPositionFromRect(eventMenuRect, { width: 720, height: 420 }),
        }}>
          <div ref={eventMenuRef}>
            <EventsSelectorMenu
              availableEvents={availableEvents}
              onSelect={handlePickEvent}
              onClose={() => setEventMenuOpen(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Global cross-entity filter: same component used in Activity/Insights.
          GlobalPropertyFilterModule fires onGlobalFilterAdd as soon as the user
          picks a property (with operator=default, value=null). The chip the
          user gets back is the EventPropertyFilterChip which is the inline
          operator + value editor. */}
      <GlobalPropertyFilterModule
        isOpen={globalFilterOpen}
        onClose={() => { setGlobalFilterOpen(false); setEditingGlobalFilter(null); }}
        triggerRef={globalFilterRef}
        teamId={teamId}
        // Restrict the property categories shown in the picker to ones that
        // actually apply to the trigger entity:
        //   events trigger → Events + Users (+ Cohorts + First time)
        //   runs/spans trigger → Agents + Spans + Users (+ Cohorts)
        // Backend agrees: cross-entity clauses are rejected (see
        // isCrossEntityClause in triggerFilterMatching.js) so without this
        // restriction users could pick props that silently never matched.
        entityScope={v.entity}
        existingFilter={editingGlobalFilter}
        onGlobalFilterAdd={(filter) => {
          if (editingGlobalFilter && editingGlobalFilter.id) {
            // Editing: merge into the existing entry.
            const next = (v.global_filters || []).map((x) =>
              x.id === editingGlobalFilter.id ? { ...x, ...filter, id: x.id } : x);
            onChange({ global_filters: next });
          } else {
            const withId = { id: filter.id || `gf-${Date.now()}-${Math.random().toString(36).slice(2,7)}`, ...filter };
            onChange({ global_filters: [...(v.global_filters || []), withId] });
          }
          setGlobalFilterOpen(false);
          setEditingGlobalFilter(null);
        }}
      />
    </div>
  );
}

// The inline filter is stored as FilterBuilder's { match, clauses:[{field,op,value}] }.
// Older patterns saved it as a flat array of { property, operator, value } chips
// (and the catalog default is { match, clauses: [] }). Normalise any of those so
// FilterBuilder always receives a valid value object. The trigger dispatcher
// accepts both shapes at evaluation time, so persistence stays backward-compatible.
export function normalizeInlineFilter(f) {
  if (f && typeof f === 'object' && !Array.isArray(f)) {
    if (Array.isArray(f.clauses)) {
      return { match: f.match || 'AND', clauses: f.clauses };
    }
    // NodeConfigModal once stored the whole FilterBuilder blob in `clauses` instead
    // of the clause array; real rows may live one level deeper.
    if (f.clauses && typeof f.clauses === 'object' && Array.isArray(f.clauses.clauses)) {
      return { match: f.clauses.match || f.match || 'AND', clauses: f.clauses.clauses };
    }
    if (f.filters && typeof f.filters === 'object' && Array.isArray(f.filters.clauses)) {
      return { match: f.filters.match || f.match || 'AND', clauses: f.filters.clauses };
    }
  }
  if (Array.isArray(f)) {
    return {
      match: 'AND',
      clauses: f.map((c) => ({
        field: c.field ?? c.property ?? '',
        op: c.op ?? c.operator ?? 'eq',
        value: c.value,
      })),
    };
  }
  return { match: 'AND', clauses: [] };
}
